import { action, makeObservable, observable } from 'mobx';
import { AdditionalPayload } from 'organisms/DataView/DataGridView/types';
import { IInvestment } from 'utils/globalTypes';
import { runRequest, safeRunRequest } from 'utils/networkRequests';

import {
  IAml,
  IAmlResponse,
  IHoldingResponse,
  IListAmlsResponse,
  IListDocumentsResponse,
  IListHoldingsResponse,
  IListOfferingsResponse,
  IListOrderActionsResponse,
  IListOrdersResponse,
  IOffering,
  IOfferingResponse,
  IOrder,
  IOrderResponse,
  IPermissionResponse,
  ITradeResponse,
  ITransactionResponse,
  IUserResponse,
  UserType
} from './networkInterfaces';

class ManagementStore {
  isDrawerOpen = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEntityUpdate: ((item: any, addPayload?: AdditionalPayload) => void) | undefined = undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedEntity: any = undefined;

  constructor() {
    makeObservable(this, {
      isDrawerOpen: observable,
      onEntityUpdate: observable,
      selectedEntity: observable,
      setSelectedEntity: action,
      fetchOffering: action,
      fetchOfferings: action,
      fetchOrders: action,
      updateOrder: action,
      fetchOrderActions: action,

      fetchHolding: action,
      createHolding: action,
      updateHolding: action,
      deleteHolding: action,

      inviteUser: action,
      deleteUser: action,
      unregisterUser: action,
      sendVerifyEmail: action,

      fetchTransaction: action,
      createTransaction: action,
      updateTransaction: action,
      deleteTransaction: action,

      fetchPermission: action,
      createPermission: action,
      updatePermission: action,
      deletePermission: action,

      createInvestorFee: action,
      updateInvestorFee: action,
      deleteInvestorFee: action,

      matchBidTrade: action,

      actionTrade: action,
      updateTrade: action,

      fetchUser: action,
      updateUser: action,

      getAml: action,
      listAmls: action,
      updateAml: action,
      listAmlDocuments: action
    });
  }

  setSelectedEntity<T extends IInvestment>(value?: T, onUdpate?: (item: T) => void) {
    this.selectedEntity = value;
    this.onEntityUpdate = onUdpate;
    this.isDrawerOpen = !!value;
  }

  async fetchOffering(offeringId: string, investorId?: string) {
    const body = { id: offeringId, investor_id: investorId };

    const response = await safeRunRequest('getOffering', body);
    return this.handleOfferingResponse(response);
  }

  async updateOffering(offeringData: IOffering) {
    const body = {
      offering: {
        type: 'Offering',
        attributes: offeringData
      }
    };

    const response = await runRequest('updateOffering', body);
    return this.handleOfferingResponse(response);
  }

  async fetchOfferings(sponsorId?: string, creatorId?: string[]) {
    const body = { sponsor_id: sponsorId, creator_id: creatorId };

    const response = await safeRunRequest('listOfferings', body);
    return this.handleOfferingListResponse(response);
  }

  async fetchOrders(offering_id: string, order_type?: number[], order_status?: number[]) {
    const body = {
      offering_id,
      ...{ order_type },
      ...{ order_status }
    };

    const response = await safeRunRequest('listOrders', body);
    return this.handleOrderListResponse(response);
  }

  async updateOrder(orderData: IOrder) {
    const body = {
      order: {
        type: 'Order',
        attributes: {
          ...orderData
        }
      }
    };

    const response = await runRequest('updateOrder', body);
    return this.handleOrderResponse(response);
  }

  async fetchOrderActions(offering_id: string, order_id: string) {
    const body = {
      offering_id,
      order_id
    };

    const response = await safeRunRequest('listOrderActions', body);
    return this.handleOrderActionListResponse(response);
  }

  async fetchUser(userId: string) {
    const body = { id: userId };

    const response = await safeRunRequest('mngGetUser', body);
    return this.handleUserActionResponse(response);
  }

  async updateUser(userId: string, userData: Record<string, unknown>) {
    const body = {
      user: {
        type: 'User',
        attributes: {
          id: userId,
          ...userData
        }
      }
    };

    const response = await runRequest('mngUpdateUser', body);
    return this.handleUserActionResponse(response);
  }

  async fetchHolding(holdingId: string) {
    const body = { id: holdingId };

    const response = await runRequest('getHolding', body);
    return this.handleHoldingActionResponse(response);
  }

  async fetchHoldings(offeringId?: string, investorId?: string) {
    const body = { offering_id: offeringId, investor_id: investorId };

    const response = await runRequest('listHoldings', body);
    return this.handleHoldingActionListResponse(response);
  }

  async createHolding(holdingData: Record<string, unknown>) {
    const body = {
      holding: {
        type: 'Holding',
        attributes: {
          ...holdingData
        }
      }
    };

    const response = await runRequest('createHolding', body);
    return this.handleHoldingActionResponse(response);
  }

  async updateHolding(holdingId: string, holdingData: Record<string, unknown>) {
    const body = {
      holding: {
        type: 'Holding',
        attributes: {
          id: holdingId,
          ...holdingData
        }
      }
    };

    const response = await runRequest('updateHolding', body);
    return this.handleHoldingActionResponse(response);
  }

  async deleteHolding(holdingId: string) {
    const body = {
      id: holdingId
    };

    const response = await runRequest('deleteHolding', body);
    return this.handleHoldingActionResponse(response);
  }

  async fetchTransaction(id: string) {
    const body = { id: id };

    const response = await runRequest('getTransaction', body);
    return this.handleTransactionActionResponse(response);
  }

  async createTransaction(transactionData: Record<string, unknown>) {
    const body = {
      transaction: {
        type: 'Transaction',
        attributes: {
          ...transactionData
        }
      }
    };

    const response = await runRequest('createTransaction', body);
    return this.handleTransactionActionResponse(response);
  }

  async updateTransaction(transactionId: string, transactionData: Record<string, unknown>) {
    const body = {
      transaction: {
        type: 'Transaction',
        attributes: {
          id: transactionId,
          ...transactionData
        }
      }
    };

    const response = await runRequest('updateTransaction', body);
    return this.handleTransactionActionResponse(response);
  }

  async deleteTransaction(transactionId: string) {
    const body = {
      id: transactionId
    };

    const response = await runRequest('deleteTransaction', body);
    return this.handleTransactionActionResponse(response);
  }

  async fetchPermission(id: string) {
    const body = { id: id };

    const response = await runRequest('getPermission', body);
    return this.handlePermissionActionResponse(response);
  }

  async createPermission(permissionData: Record<string, unknown>, send_notification?: boolean) {
    const body = {
      permission: {
        type: 'Permission',
        attributes: {
          ...permissionData
        }
      },
      send_notification
    };

    const response = await runRequest('createPermission', body);
    return this.handlePermissionActionResponse(response);
  }

  async updatePermission(
    permissionId: string,
    permissionData: Record<string, unknown>,
    send_notification?: boolean
  ) {
    const body = {
      permission: {
        type: 'Permission',
        attributes: {
          id: permissionId,
          ...permissionData
        }
      },
      send_notification
    };

    const response = await runRequest('updatePermission', body);
    return this.handlePermissionActionResponse(response);
  }

  async deletePermission(permissionId: string) {
    const body = {
      id: permissionId
    };

    const response = await runRequest('deletePermission', body);
    return this.handlePermissionActionResponse(response);
  }

  async createInvestorFee(feeData: Record<string, unknown>) {
    const body = {
      investor_fee: {
        type: 'InvestorFee',
        attributes: {
          ...feeData
        }
      }
    };

    const response = await runRequest('createInvestorFee', body);
    return this.handlePermissionActionResponse(response);
  }

  async updateInvestorFee(feeId: string, feeData: Record<string, unknown>) {
    const body = {
      investor_fee: {
        type: 'InvestorFee',
        attributes: {
          id: feeId,
          ...feeData
        }
      }
    };

    const response = await runRequest('updateInvestorFee', body);
    return this.handlePermissionActionResponse(response);
  }

  async deleteInvestorFee(feeId: string) {
    const body = {
      id: feeId
    };

    const response = await runRequest('deleteInvestorFee', body);
    return this.handlePermissionActionResponse(response);
  }

  async matchBidTrade({
    offeringId,
    buyOrderId,
    sellOrderId,
    notifyUser
  }: {
    offeringId: string;
    buyOrderId: string;
    sellOrderId?: string;
    notifyUser?: boolean;
  }) {
    const body = {
      offering_id: offeringId,
      buy_order_id: buyOrderId,
      sell_order_id: sellOrderId,
      investor_silent: !notifyUser
    };

    const response = await runRequest('matchBidTrade', body);
    return this.handleTradeResponse(response);
  }

  async matchIntTrade({
    offeringId,
    buyOrderId,
    sellHoldingId,
    notifyUser
  }: {
    offeringId: string;
    buyOrderId: string;
    sellHoldingId: string;
    notifyUser?: boolean;
  }) {
    const body = {
      offering_id: offeringId,
      buy_order_id: buyOrderId,
      sell_holding_id: sellHoldingId,
      investor_silent: !notifyUser
    };

    const response = await runRequest('matchIntTrade', body);
    return this.handleTradeResponse(response);
  }

  actionTrade(offeringId: string, tradeId: string, action: string, notifyUser?: boolean) {
    const body = {
      offering_id: offeringId,
      id: tradeId,
      investor_silent: !notifyUser
    };

    return runRequest(action, body).then((response) => this.handleTradeResponse(response));
  }

  async updateTrade(tradeId: string, tradeData: Record<string, unknown>) {
    const body = {
      trade: {
        type: 'Trade',
        attributes: {
          id: tradeId,
          ...tradeData
        }
      }
    };

    const response = await runRequest('updateTrade', body);
    return this.handleTradeResponse(response);
  }

  async unregisterUser(userId: string) {
    const body = { id: userId };

    const response = await runRequest('mngUnregisterUser', body);
    return this.handleUserActionResponse(response);
  }

  async deleteUser(userId: string) {
    const body = { id: userId };

    const response = await runRequest('mngDeleteUser', body);
    return this.handleUserActionResponse(response);
  }

  async sendVerifyEmail(userId: string) {
    const body = { id: userId };

    const response = await runRequest('mngSendVerifyEmail', body);
    return this.handleUserActionResponse(response);
  }

  async inviteUser(
    email: string,
    sponsorId?: string | null,
    userType?: UserType,
    sendEmail?: boolean
  ) {
    const body = {
      email,
      sponsor_id: sponsorId,
      user_type: userType,
      send_email: sendEmail
    };

    const response = await runRequest('mngInviteUser', body);
    return this.handleUserActionResponse(response);
  }

  handleAmlResponse(response: IAmlResponse) {
    return response?.data?.attributes;
  }

  async updateAml(aml: IAml) {
    const body = {
      aml: {
        type: 'Aml',
        attributes: aml
      }
    };

    const response = await safeRunRequest('mngUpdateAml', body);
    return this.handleAmlResponse(response);
  }

  getAml(amlId: string) {
    const body = {
      id: amlId
    };

    return safeRunRequest('mngGetAml', body).then((response) => this.handleAmlResponse(response));
  }

  listAmls(investorId: string) {
    const body = {
      investor_id: investorId
    };

    return safeRunRequest('mngListAmls', body).then((response) =>
      this.handleAmlListResponse(response)
    );
  }

  listAmlDocuments(amlId: string) {
    const body = {
      aml_id: amlId
    };

    return safeRunRequest('mngListDocuments', body).then((response) =>
      this.handleDocumentListResponse(response)
    );
  }

  handleAmlListResponse(response: IListAmlsResponse) {
    return {
      total: response?.total,
      data: response?.data
        ? response?.data?.map((aml) => {
            return { ...aml.attributes };
          })
        : []
    };
  }

  handleDocumentListResponse(response: IListDocumentsResponse) {
    return {
      total: response?.total,
      data: response?.data
        ? response?.data?.map((document) => {
            return { ...document.attributes };
          })
        : []
    };
  }

  handleOfferingResponse(response: IOfferingResponse) {
    return response?.data?.attributes;
  }

  handleTradeResponse(response: ITradeResponse) {
    return response?.data?.attributes;
  }

  handleOfferingListResponse(response: IListOfferingsResponse) {
    return response?.data ? response?.data?.map((offering) => offering.attributes) : [];
  }

  handleOrderListResponse(response: IListOrdersResponse) {
    return response?.data ? response?.data?.map((order) => order.attributes) : [];
  }

  handleOrderActionListResponse(response: IListOrderActionsResponse) {
    return response?.data ? response?.data?.map((orderAction) => orderAction.attributes) : [];
  }

  handleUserActionResponse(response: IUserResponse) {
    return response?.data?.attributes;
  }

  handleHoldingActionResponse(response: IHoldingResponse) {
    return response?.data?.attributes;
  }

  handleOrderResponse(response: IOrderResponse) {
    return response?.data?.attributes;
  }

  handleHoldingActionListResponse(response: IListHoldingsResponse) {
    return response?.data ? response?.data?.map((orderAction) => orderAction.attributes) : [];
  }

  handleTransactionActionResponse(response: ITransactionResponse) {
    return response?.data?.attributes;
  }

  handlePermissionActionResponse(response: IPermissionResponse) {
    return response?.data?.attributes;
  }
}

export default ManagementStore;
